import DOMPurify from 'dompurify'
import { FC, ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Card, CardContent, Divider, Stack, Typography } from '@mui/material'

import { Analyst } from '../../../../../components/Analyst/Analyst'
import { HtmlContentParser } from '../../../../../components/HtmlContentParser/HtmlContentParser'
import { Analyst as TAnalyst } from '../../../../../types/Analyst'

export type ReviewPreviewSection = { key: string; heading?: string; html?: string | null }

type ReviewPreviewProps = {
  title: string
  subtitle: string
  analyst?: TAnalyst
  sections: ReviewPreviewSection[]
  actions?: ReactNode
}

// Like SaaS's review card. Links aren't clickable: screenshot links only work in SaaS.
export const ReviewPreview: FC<ReviewPreviewProps> = ({ title, subtitle, analyst, sections, actions }) => {
  const { t } = useTranslation()
  const visibleSections = useMemo(
    () => sections.flatMap((section) => (section.html ? [{ ...section, html: DOMPurify.sanitize(section.html) }] : [])),
    [sections]
  )

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Typography variant="h6">{t('internal-analysts:review_preview')}</Typography>
            {actions}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t('internal-analysts:review_preview_help')}
          </Typography>
          <Divider />
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h3">{title}</Typography>
              <Typography variant="h5">{subtitle}</Typography>
            </Box>
            {analyst && <Analyst name={analyst.name} picture={analyst.picture} title={analyst.title} direction="row-reverse" sx={{ width: 'auto' }} />}
          </Stack>
          <Box sx={{ pointerEvents: 'none' }} data-testid="review-preview-content">
            {visibleSections.length ? (
              visibleSections.map(({ key, heading, html }) => (
                <Box key={key} mb={2}>
                  {heading && <Typography variant="h3">{heading}</Typography>}
                  <HtmlContentParser rawHtml={html} />
                </Box>
              ))
            ) : (
              <Typography color="text.secondary">{t('internal-analysts:review_preview_empty')}</Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}
